import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { Product, ProductAdminItem } from '../../shared/models/Product';
import { SpringPage } from '../../shared/models/page';
import { environment } from '../../../environments/environment.dev';

export interface ProductCatalogFilters {
  categoryId?: number | null;
  brandId?: number | null;
  modeloId?: number | null;
  sizeId?: number | null;
  color?: string | null;
  q?: string | null;
  inStock?: boolean | null;
  sort?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class Products {

  private apiUrl = `${environment.apiUrl}/products`;

  constructor(
    private http: HttpClient
  ) {}

  getAllProducts(): Observable<Product[]> {
    return this.http.get<Product[]>(this.apiUrl);
  }

  getCatalogProducts(filters: ProductCatalogFilters = {}): Observable<Product[]> {
    let params = new HttpParams();
    if (filters.categoryId != null) params = params.set('categoryId', String(filters.categoryId));
    if (filters.brandId != null) params = params.set('brandId', String(filters.brandId));
    if (filters.modeloId != null) params = params.set('modeloId', String(filters.modeloId));
    if (filters.sizeId != null) params = params.set('sizeId', String(filters.sizeId));
    if (filters.color) params = params.set('color', filters.color);
    if (filters.q && filters.q.trim()) params = params.set('q', filters.q.trim());
    if (filters.inStock != null) params = params.set('inStock', String(filters.inStock));
    if (filters.sort) params = params.set('sort', filters.sort);
    return this.http.get<Product[]>(this.apiUrl, { params });
  }

  getAdminProducts(): Observable<Product[]> {
    return this.http.get<Product[]>(`${this.apiUrl}/all`);
  }

  getAdminPage(page = 0, size = 10, q?: string, active?: boolean | null): Observable<SpringPage<ProductAdminItem>> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));
    if (q?.trim()) params = params.set('q', q.trim());
    if (active !== undefined && active !== null) params = params.set('active', String(active));
    return this.http.get<SpringPage<ProductAdminItem>>(`${this.apiUrl}/admin`, { params });
  }

  searchProducts(q: string): Observable<Product[]> {
    if (!q || q.trim().length < 2) return of([]);
    return this.http.get<Product[]>(`${this.apiUrl}/search`, { params: { q: q.trim() } });
  }

  getProductById(id: number): Observable<Product> {
    return this.http.get<Product>(`${this.apiUrl}/${id}`);
  }

  getProductBySlug(slug: string): Observable<Product> {
    return this.http.get<Product>(`${this.apiUrl}/slug/${slug}`);
  }

  createProduct(product: any): Observable<any> {
    return this.http.post<any>(this.apiUrl, product);
  }

  updateProduct(id: number, product: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${id}`, product);
  }

  deleteProduct(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  activateProduct(id: number): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/${id}/activate`, {});
  }

  addStock(stockData: { productId: number, sizeId: number, quantityToAdd: number }): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/stock/add`, stockData);
  }
}